import React, { useState } from "react";
import { type AwardData, type FeaturedData } from "../../utils/mockProjects";
import {
  useMotionValue,
  useSpring,
  useTransform,
  motion,
  AnimatePresence,
} from "motion/react";
import { HighLightText } from "../../utils/HighLightText";

interface RecognitionProps {
  awards?: AwardData[];
  featured?: FeaturedData[];
}
export const Recognition: React.FC<RecognitionProps> = ({
  awards,
  featured,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [showAllAwards, setShowAllAwards] = useState(false);
  const [showAllFeatured, setShowAllFeatured] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useSpring(0, { stiffness: 400, damping: 20 });
  const rawSkew = useSpring(0, { stiffness: 400, damping: 20 }); // actual value you control
  // const transform = useTransform(
  //   [x, y, scale, rawSkew],
  //   ([xVal, yVal, scaleVal, skew]) =>
  //     `translate3d(${xVal}px, ${yVal}px, 0) scale(${scaleVal}) skewX(${skew}deg)`
  // );
  const transform = useTransform(
    [x, y, scale],
    ([xVal, yVal, scaleVal]) =>
      `translate3d(${xVal}px, ${yVal}px, 0) scale(${scaleVal})`,
  );

  const prevX = React.useRef<number | null>(null);
  const skewTimeout = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (hoveredIdx !== null) {
      scale.set(1.15); // jelly pop
    } else {
      scale.set(0);
      rawSkew.set(0);
      prevX.current = null;
      if (skewTimeout.current) {
        clearTimeout(skewTimeout.current);
        skewTimeout.current = null;
      }
    }
  }, [hoveredIdx, scale, rawSkew]);

  // Mouse move handler for floating image with skew
  const handleMouseMove = (e: React.MouseEvent) => {
    x.set(e.clientX + 54);
    y.set(e.clientY);

    if (prevX.current !== null) {
      const dx = e.clientX - prevX.current;
      // Clamp skew between -20deg and 20deg for effect
      const newSkew = Math.max(-20, Math.min(20, dx * -0.3));
      rawSkew.set(newSkew);
      if (skewTimeout.current) clearTimeout(skewTimeout.current);
      skewTimeout.current = setTimeout(() => rawSkew.set(0), 120);
    }
    prevX.current = e.clientX;
  };

  const renderAwards = () =>
    awards ? (
      <div className="w-full flex items-center justify-center mt-4">
        <div className="w-full flex md:flex-row flex-col md:gap-0 gap-8 justify-between h-fit">
          <div className="w-full h-fit flex lg:gap-0 gap-1 lg:flex-col lg:text-2xl md:text-xl text-lg text-primary font-gilroy-bold">
            Awards & <span>Recognition</span>
          </div>

          <div className="w-full h-fit lg:pr-[10vw] flex-col flex justify-center">
            {(showAllAwards ? awards : awards.slice(0, 4)).map((item, index) => (
              <div
                key={index}
                className={`${
                  index === 0 ? "" : "lg:mt-16 mt-8"
                } z-10 lg:pb-8 pb-6 font-gilroy-light capitalize border-b-[1px] border-primary leading-[1.2] h-fit lg:text-[32px] text-[24px] relative`}
                onMouseEnter={() => setHoveredIdx(index)}
                onMouseLeave={() => setHoveredIdx(null)}
                onMouseMove={handleMouseMove}
              >
                {HighLightText(item.award, "font-gilroy-bold")}
              </div>
            ))}
            {awards.length > 4 && (
              <button
                className="mt-4 px-4 py-2 bg-primary text-white rounded font-gilroy-bold w-fit self-start"
                onClick={() => setShowAllAwards((prev) => !prev)}
              >
                {showAllAwards ? "Show Less" : "Read More"}
              </button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {hoveredIdx !== null && (
            <motion.img
              src={awards?.[hoveredIdx].image}
              alt="award"
              style={{
                transform,
                position: "fixed",
                pointerEvents: "none",
                left: 0,
                top: 0,
              }}
              animate={{
                opacity: 1,
                filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.18))",
              }}
              initial={{ scale: 0.7, opacity: 0 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 20,
                scale: { type: "spring", stiffness: 200, damping: 20 },
              }}
              width={150}
              height={150}
            />
          )}
        </AnimatePresence>
      </div>
    ) : null;

  const renderFeatured = () =>
    featured ? (
      <div className="w-full mt-4 flex items-center justify-center">
        <div className="w-full flex md:flex-row flex-col md:gap-0 gap-8 justify-between h-fit">
          <div className="w-full h-fit lg:text-2xl md:text-xl text-lg text-primary font-gilroy-bold">
            Featured In
          </div>

          <div className="w-full h-fit lg:pr-[10vw] flex-col flex justify-center">
            {(showAllFeatured ? featured : featured.slice(0, 4)).map((item, index) => (
              <div
                onClick={() => window.open(item.link, "_blank")}
                key={index}
                className={`${
                  index === 0 ? "" : "lg:mt-16 mt-8"
                } z-10 lg:pb-8 pb-6 cursor-pointer font-gilroy-light capitalize border-b-[1px] border-primary leading-[1.2] h-fit lg:text-[32px] text-[24px] relative`}
              >
                {item.title}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-external-link-icon text-blue-500 place-self-end lucide-external-link"
                >
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
              </div>
            ))}
            {featured.length > 4 && (
              <button
                className="mt-4 px-4 py-2 bg-primary text-white rounded font-gilroy-bold w-fit self-start"
                onClick={() => setShowAllFeatured((prev) => !prev)}
              >
                {showAllFeatured ? "Show Less" : "Read More"}
              </button>
            )}
          </div>
        </div>
      </div>
    ) : null;

  if (!awards && !featured) return null;

  return (
    <>
      {renderAwards()}
      {renderFeatured()}
    </>
  );
};
